import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

function CandidatesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const navigate = useNavigate();
  const { admin, token, logout, loading: authLoading } = useAuth();

  // Read state from URL params
  const page = parseInt(searchParams.get('page')) || 1;
  const sortBy = searchParams.get('sortBy') || 'submittedAt';
  const order = searchParams.get('order') || 'desc';
  const statusFilter = searchParams.get('status') || '';
  const flagsFilter = searchParams.get('flags') || '';
  const search = searchParams.get('search') || '';

  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) next.delete(k);
        else next.set(k, v);
      });
      return next;
    });
  }, [setSearchParams]);

  const handleSort = (field) => {
    if (sortBy === field) {
      updateParams({ order: order === 'asc' ? 'desc' : 'asc', page: 1 });
    } else {
      updateParams({ sortBy: field, order: 'desc', page: 1 });
    }
  };

  const sortArrow = (field) => {
    if (sortBy !== field) return ' \u2195';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  useEffect(() => {
    if (!authLoading && !admin) navigate('/admin/login');
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      try {
        const params = new URLSearchParams({ page, limit: 50, sortBy, order });
        if (statusFilter) params.set('status', statusFilter);
        if (flagsFilter) params.set('flags', flagsFilter);
        if (search) params.set('search', search);
        const res = await axios.get(`/api/submissions/all?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmissions(res.data.submissions || res.data || []);
        setTotalPages(res.data.totalPages || 1);
        setTotal(res.data.total || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, page, sortBy, order, statusFilter, flagsFilter, search]);

  const groupedByTest = {};
  submissions.forEach(sub => {
    const testId = sub.test?._id || 'unknown';
    const testTitle = sub.test?.title || 'Unknown Test';
    if (!groupedByTest[testId]) {
      groupedByTest[testId] = { title: testTitle, submissions: [] };
    }
    groupedByTest[testId].submissions.push(sub);
  });

  if (authLoading || loading) {
    return (
      <div className="admin-layout">
        <aside className="sidebar"><div className="sidebar-header"><h2>Exam System</h2></div></aside>
        <main className="admin-main"><div className="loading-spinner">Loading...</div></main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <aside className="sidebar">
        <div className="sidebar-header"><h2>Exam System</h2></div>
        <nav className="sidebar-nav">
          <Link to="/admin/dashboard" className="nav-link">Dashboard</Link>
          <Link to="/admin/create-test" className="nav-link">Create Test</Link>
          <Link to="/admin/candidates" className="nav-link active">Candidates</Link>
        </nav>
        <div className="sidebar-footer">
          <div className="admin-info">
            <span className="admin-name">{admin?.name || admin?.email}</span>
          </div>
          <button className="btn btn-logout" onClick={logout}>Logout</button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-header">
          <h1>All Candidates ({total})</h1>
        </div>

        {/* Search & Filter Bar */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <form onSubmit={(e) => { e.preventDefault(); updateParams({ search: searchInput, page: 1 }); }} style={{ display: 'flex', gap: '0.5rem', flex: 1, minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem', flex: 1 }}
            />
            <button type="submit" className="btn btn-primary btn-sm">Search</button>
            {search && (
              <button type="button" className="btn btn-outline btn-sm" onClick={() => { setSearchInput(''); updateParams({ search: '', page: 1 }); }}>Clear</button>
            )}
          </form>
          <select
            value={statusFilter}
            onChange={(e) => updateParams({ status: e.target.value, page: 1 })}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
          >
            <option value="">All Status</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={flagsFilter}
            onChange={(e) => updateParams({ flags: e.target.value, page: 1 })}
            style={{ padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
          >
            <option value="">All Integrity</option>
            <option value="flagged">Has Flags</option>
            <option value="clean">Clean</option>
          </select>
        </div>

        {submissions.length === 0 && !search && !statusFilter && !flagsFilter ? (
          <div className="empty-state">
            <h3>No submissions yet</h3>
            <p>Candidates will appear here once they submit exams.</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="empty-state">
            <h3>No results</h3>
            <p>No submissions match your search or filter criteria.</p>
          </div>
        ) : (
          <div className="candidates-by-test">
            {Object.entries(groupedByTest).map(([testId, group]) => (
              <div key={testId} className="test-group">
                <div className="test-group-header">
                  <h2>{group.title}</h2>
                  <span className="test-group-count">{group.submissions.length} candidate{group.submissions.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="table-wrapper" style={{ padding: '1rem' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('totalScore')}>Score{sortArrow('totalScore')}</th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('percentage')}>Percentage{sortArrow('percentage')}</th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('passed')}>Status{sortArrow('passed')}</th>
                        <th>Integrity</th>
                        <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('submittedAt')}>Submitted{sortArrow('submittedAt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.submissions.map(sub => {
                        const c = sub.candidate || {};
                        return (
                          <tr
                            key={sub._id}
                            className="clickable-row"
                            onClick={() => { if (c._id) navigate(`/admin/candidate/${c._id}`); }}
                          >
                            <td><strong>{c.name || 'Unnamed'}</strong></td>
                            <td>{c.email || 'N/A'}</td>
                            <td>{sub.scoringStatus === 'completed' ? `${Number(sub.totalScore || 0).toFixed(1)}/${sub.maxScore || 0}` : '...'}</td>
                            <td>{sub.scoringStatus === 'completed' ? `${Math.round(sub.percentage || 0)}%` : '...'}</td>
                            <td>
                              {sub.scoringStatus === 'completed' ? (
                                <span className={`status-badge ${sub.passed ? 'status-pass' : 'status-fail'}`}>
                                  {sub.passed ? 'Passed' : 'Failed'}
                                </span>
                              ) : (
                                <span className="status-badge" style={{ background: '#f59e0b', color: '#fff' }}>
                                  {sub.scoringStatus === 'failed' ? 'Scoring Failed' : 'Scoring...'}
                                </span>
                              )}
                            </td>
                            <td>
                              {sub.cheatFlags?.length > 0
                                ? <span className="cheat-flag-count">{sub.cheatFlags.length} flags</span>
                                : <span className="no-flags">Clean</span>
                              }
                            </td>
                            <td>{sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {totalPages > 1 && (
              <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => updateParams({ page: Math.max(1, page - 1) })}
                  disabled={page <= 1}
                >
                  &larr; Previous
                </button>
                <span style={{ fontSize: '0.9rem' }}>
                  Page {page} of {totalPages} ({total} total)
                </span>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => updateParams({ page: Math.min(totalPages, page + 1) })}
                  disabled={page >= totalPages}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default CandidatesList;
